import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import DeleteIcon from "~/components/Icons/svg/delete";
import DocxIcon from "~/components/Icons/svg/docx";
import RedesignButton from "~/shared/ui/Fields/RedesignButton";
import { uploadFile } from "~/libs/uploadFile";
import { ContractTemplateTableRow } from "~/pages/Editor/ContractTemplates/model/tableAdapter";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

import s from "./ContractTemplatesForm.less";

type ActiveOption = {
  value: string;
  label: string;
};

type DisplayFile = {
  id: string;
  name: string;
  link: string;
};

type ContractTemplatesFormData = {
  title: string;
  fileId: string;
  description: ActiveOption;
};

interface ContractTemplatesFormProps {
  currentItem?: ContractTemplateTableRow;
  onSubmit: (data: ContractTemplatesFormData) => void;
  formId: string;
}

const ContractTemplatesForm = ({
  currentItem,
  onSubmit,
  formId,
}: ContractTemplatesFormProps) => {
  const { t } = useTranslation();

  const [uploadFileLoader, setUploadFileLoader] = useState(false);
  const { notifySuccess, notifyError } = useNotify();

  const currentFile = currentItem?.File as DisplayFile | undefined;

  const [displayFile, setDisplayFile] = useState<DisplayFile | null>(
    currentFile ?? null,
  );

  const schema = yup.object().shape({
    title: yup.string().required(t("validation.required")),
    fileId: yup.string().required(t("validation.required")),
  });

  const activeOptions: ActiveOption[] = [
    { value: "active", label: "Активный" },
    { value: "inactive", label: "Неактивный" },
  ];

  const getActiveOption = () => {
    if (!currentItem) return activeOptions[0];
    const isActive = String(currentItem.active) !== "НЕАКТИВНЫЙ";
    return (
      activeOptions.find(
        (opt) => opt.value === (isActive ? "active" : "inactive"),
      ) || activeOptions[0]
    );
  };

  const { handleSubmit, control, errors, setValue, register, clearErrors } =
    useForm<ContractTemplatesFormData>({
      resolver: yupResolver(schema),
      defaultValues: {
        title: currentItem?.name || "",
        fileId: currentItem?.FileId || "",
        description: getActiveOption(),
      },
    });

  useEffect(() => {
    // fileId не привязан к видимому контролу (файл грузится через
    // FileField-пикер), поэтому регистрируем поле вручную — иначе setValue
    // его не обновит и в onSubmit оно не попадёт.
    register("fileId");
  }, [register]);

  // Заполняем форму только при смене редактируемой записи (по id), а не по
  // ссылке на объект: страница пересобирает массив contracts на каждый свой
  // рендер (например, при показе нотификации после загрузки файла), и зависимость
  // от currentItem сбрасывала бы выбранный файл обратно на прикреплённый.
  const currentItemId = currentItem?.id;

  useEffect(() => {
    if (currentItem) {
      setValue("title", currentItem.name || "");
      setValue("fileId", currentItem.FileId || "");
      setDisplayFile((currentItem.File as DisplayFile | undefined) ?? null);
    }
  }, [currentItemId, setValue]);

  const handleDeleteFile = () => {
    setDisplayFile(null);
    setValue("fileId", "");
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="title"
          control={control}
          label={t("contractTemplates.table.name")}
          error={errors["title"]?.message}
        />
        {displayFile ? (
          <Flex
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
          >
            <Flex align="center" gap={8}>
              <DocxIcon size="20" />
              <a
                className={s.uploadFileName}
                href={displayFile.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {displayFile.name}
              </a>
            </Flex>
            {/* В режиме редактирования файл только показываем — удалить или
                заменить его нельзя. */}
            {!currentItem && (
              <RedesignButton
                txtBtn={t("ui.delete")}
                theme="empty-accent"
                customClassName={s.buttonDelete}
                onClick={handleDeleteFile}
                iconComponent={
                  <DeleteIcon width={14} height={15} fill="#E37639" />
                }
              />
            )}
          </Flex>
        ) : (
          <Field
            type="file"
            name="file"
            control={control}
            label={t("ui.upload_file")}
            error={errors["fileId"]?.message}
            accept={[
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ]}
            loading={uploadFileLoader}
            onUpload={async (file) => {
              setUploadFileLoader(true);
              try {
                const uploaded = await uploadFile(file, () =>
                  notifyError(t("common.data_uploaded_error")),
                );
                if (uploaded) {
                  // Режим пикера: значение FileField в форме не храним,
                  // id загруженного файла пишем в fileId сами.
                  setValue("fileId", uploaded.id);
                  clearErrors("fileId");
                  setDisplayFile({
                    id: uploaded.id,
                    name: uploaded.name,
                    link: uploaded.link,
                  });
                  notifySuccess(t("common.file_uploaded_successfully"));
                }
              } finally {
                setUploadFileLoader(false);
              }
            }}
          />
        )}
        <Field
          type="select"
          name="description"
          control={control}
          label={t("contractTemplates.table.active")}
          options={activeOptions}
          labelInValue
        />
      </Flex>
    </form>
  );
};

export default ContractTemplatesForm;
